import { useCallback, useEffect, useState } from 'react';
import type { VaultTag, VaultTagLogEntry } from '@todo/shared';
import { ApiError } from '../api/client';
import { vaultApi } from '../api/vault.api';
import { SidePanel } from './SidePanel';

/**
 * Manage Folder's tags: rename, delete, and read the change history.
 *
 * The tag list is SHARED with CG Sign (one list per organisation), so a
 * rename or delete here shows there too - the delete warning says how many
 * other items are affected. Files point at a tag by id, so a rename needs
 * no follow-up: every file shows the new name the next time it loads.
 * New tags are added with the box at the top of the Tags tab, or by naming
 * them in a file's Tags panel.
 */

type Tab = 'tags' | 'history';

const ACTION_LABEL: Record<VaultTagLogEntry['action'], string> = {
  TAG_CREATED: 'Tag created',
  TAG_RENAMED: 'Tag renamed',
  TAG_DELETED: 'Tag deleted',
};

function when(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    .format(new Date(iso));
}

function Row({
  tag,
  onRename,
  onRemove,
}: {
  tag: VaultTag;
  onRename: (id: string, name: string) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tag.name);
  const [confirming, setConfirming] = useState(false);
  const [confirmRename, setConfirmRename] = useState(false);
  const [busy, setBusy] = useState(false);
  const files = tag.fileCount ?? 0;
  const elsewhere = tag.elsewhereCount ?? 0;
  const inUse = files + elsewhere > 0;
  const usedOn = [
    files > 0 ? `${files} ${files === 1 ? 'Folder file' : 'Folder files'}` : '',
    elsewhere > 0 ? `${elsewhere} ${elsewhere === 1 ? 'CG Sign document' : 'CG Sign documents'}` : '',
  ].filter(Boolean).join(' and ');

  async function save(confirmed = false) {
    const next = name.trim();
    if (!next || next === tag.name) { setEditing(false); setConfirmRename(false); setName(tag.name); return; }
    // A rename of a tag that is on anything shows where it applies first.
    if (inUse && !confirmed) { setConfirmRename(true); return; }
    setBusy(true);
    try { await onRename(tag.id, next); setEditing(false); setConfirmRename(false); }
    finally { setBusy(false); }
  }

  async function remove() {
    setBusy(true);
    try { await onRemove(tag.id); }
    finally { setBusy(false); setConfirming(false); }
  }

  return (
    <li className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="flex items-center gap-2">
        {editing ? (
          <input
            autoFocus
            value={name}
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void save();
              if (e.key === 'Escape') { setEditing(false); setName(tag.name); }
            }}
            className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm outline-none focus:border-brand-500"
          />
        ) : (
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">#{tag.name}</span>
        )}
        <span className="shrink-0 text-xs text-slate-400">
          {files} {files === 1 ? 'file' : 'files'}
        </span>
        {editing ? (
          <>
            <button type="button" disabled={busy} onClick={() => void save()}
              className="rounded-md bg-brand-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50">Save</button>
            <button type="button" onClick={() => { setEditing(false); setConfirmRename(false); setName(tag.name); }}
              className="rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100">Cancel</button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setEditing(true)} aria-label={`Rename ${tag.name}`}
              className="rounded-md px-2 py-1 text-xs text-slate-600 hover:bg-slate-100">Rename</button>
            <button type="button" onClick={() => setConfirming(true)} aria-label={`Delete ${tag.name}`}
              className="rounded-md px-2 py-1 text-xs text-red-600 hover:bg-red-50">Delete</button>
          </>
        )}
      </div>

      {confirmRename && (
        <div className="mt-2 rounded-md bg-amber-50 p-2.5 text-xs text-amber-900">
          <p>
            Renaming applies everywhere this tag is used: {usedOn}. The tag list is shared with CG Sign,
            so they all show the new name.
          </p>
          <div className="mt-2 flex gap-2">
            <button type="button" disabled={busy} onClick={() => void save(true)}
              className="rounded-md bg-brand-600 px-2.5 py-1 font-semibold text-white disabled:opacity-50">Rename everywhere</button>
            <button type="button" onClick={() => setConfirmRename(false)}
              className="rounded-md px-2.5 py-1 text-slate-600 hover:bg-white">Cancel</button>
          </div>
        </div>
      )}

      {confirming && (inUse ? (
        <div className="mt-2 rounded-md bg-amber-50 p-2.5 text-xs text-amber-900">
          <p>
            This tag can't be deleted while it is in use: {usedOn}. Remove it from them first.
          </p>
          <div className="mt-2">
            <button type="button" onClick={() => setConfirming(false)}
              className="rounded-md px-2.5 py-1 text-slate-600 hover:bg-white">Close</button>
          </div>
        </div>
      ) : (
        <div className="mt-2 rounded-md bg-red-50 p-2.5 text-xs text-red-800">
          <p>No file or document uses this tag. Delete it?</p>
          <div className="mt-2 flex gap-2">
            <button type="button" disabled={busy} onClick={() => void remove()}
              className="rounded-md bg-red-600 px-2.5 py-1 font-semibold text-white disabled:opacity-50">Delete</button>
            <button type="button" onClick={() => setConfirming(false)}
              className="rounded-md px-2.5 py-1 text-slate-600 hover:bg-white">Keep it</button>
          </div>
        </div>
      ))}
    </li>
  );
}

export function VaultTagsManager({
  open,
  onClose,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  /** Reload whatever shows tag names: the file list and the filters. */
  onChanged: () => void;
}) {
  const [tab, setTab] = useState<Tab>('tags');
  const [tags, setTags] = useState<VaultTag[] | null>(null);
  const [log, setLog] = useState<VaultTagLogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const loadTags = useCallback(() => {
    vaultApi.tags().then(setTags).catch(() => setTags([]));
  }, []);
  const loadLog = useCallback(() => {
    vaultApi.tagLog().then(setLog).catch(() => setLog([]));
  }, []);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setLog(null);
    loadTags();
  }, [open, loadTags]);

  useEffect(() => {
    if (open && tab === 'history') loadLog();
  }, [open, tab, loadLog]);

  async function guard(work: () => Promise<void>) {
    setError(null);
    try {
      await work();
      loadTags();
      setLog(null);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work - try again.');
    }
  }

  async function add() {
    const name = newName.trim().replace(/^#+/, '').trim();
    if (!name || adding) return;
    setAdding(true);
    await guard(async () => { await vaultApi.createTag(name); setNewName(''); });
    setAdding(false);
  }

  return (
    <SidePanel
      open={open}
      title="Tags"
      description="Add, rename or delete tags, and see who changed what. The list is shared with CG Sign."
      onClose={onClose}
    >
      <div className="flex h-full flex-col px-6 py-5">
        <div className="mb-3 flex gap-1 rounded-lg bg-slate-100 p-1 text-sm">
          {(['tags', 'history'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => { setTab(t); setError(null); }}
              className={`flex-1 rounded-md px-3 py-1.5 font-medium capitalize ${tab === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
            >
              {t}
            </button>
          ))}
        </div>

        {error && (
          <p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">{error}</p>
        )}

        {tab === 'tags' && (
          <div className="mb-3 flex gap-2">
            <input
              value={newName}
              maxLength={40}
              placeholder="New tag name"
              aria-label="New tag name"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
              className="min-w-0 flex-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-brand-500"
            />
            <button
              type="button"
              disabled={adding || newName.trim() === ''}
              onClick={() => void add()}
              className="shrink-0 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              Add tag
            </button>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {tab === 'tags' && (
            tags === null ? (
              <p className="py-6 text-center text-sm text-slate-400">Loading…</p>
            ) : tags.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">No tags yet - add one above.</p>
            ) : (
              <ul className="space-y-2">
                {tags.map((tag) => (
                  <Row
                    key={tag.id}
                    tag={tag}
                    onRename={(id, name) => guard(async () => { await vaultApi.renameTag(id, name); })}
                    onRemove={(id) => guard(async () => { await vaultApi.removeTag(id); })}
                  />
                ))}
              </ul>
            )
          )}

          {tab === 'history' && (
            log === null ? (
              <p className="py-6 text-center text-sm text-slate-400">Loading…</p>
            ) : log.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">Nothing has been changed yet.</p>
            ) : (
              <ul className="space-y-2">
                {log.map((e) => (
                  <li key={e.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                    <p className="font-medium text-slate-800">
                      {ACTION_LABEL[e.action] ?? e.action}
                      <span className="font-normal text-slate-600">
                        {e.oldName && e.newName ? ` - ${e.oldName} → ${e.newName}` : ''}
                        {e.oldName && !e.newName ? ` - ${e.oldName}` : ''}
                        {!e.oldName && e.newName ? ` - ${e.newName}` : ''}
                      </span>
                    </p>
                    {e.detail && <p className="text-xs text-slate-500">{e.detail}</p>}
                    <p className="text-xs text-slate-400">{e.actor} · {when(e.createdAt)}</p>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>
      </div>
    </SidePanel>
  );
}
